import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Check, Gauge, Zap, Fuel, ChevronLeft, Loader2, Tag } from "lucide-react";

const PLANS = [
  { key: "weekly", label: "Weekly", unit: "week", field: "weekly_rate" },
  { key: "monthly", label: "Monthly", unit: "month", field: "monthly_rate" },
];

export default function BikeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bike, setBike] = useState(null);
  const [plan, setPlan] = useState("weekly");
  const [quantity, setQuantity] = useState(1);
  const [startDate, setStartDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [submitting, setSubmitting] = useState(false);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    api.get(`/bikes/${id}`).then((res) => setBike(res.data)).catch(() => navigate("/bikes"));
  }, [id, navigate]);

  if (!bike) return <div className="min-h-[60vh] grid place-items-center text-muted-foreground">Loading…</div>;

  const rate = bike[PLANS.find((p) => p.key === plan).field];
  const unit = PLANS.find((p) => p.key === plan).unit;
  const total = (rate * Math.max(1, quantity)).toFixed(2);

  let bookButtonLabel = "Book & Pay Now";
  if (submitting) bookButtonLabel = null;
  else if (!bike.available) bookButtonLabel = "Currently Unavailable";

  const handleBook = async () => {
    if (!user) {
      toast.info("Please log in to book a bike.");
      navigate("/login", { state: { from: `/bikes/${id}` } });
      return;
    }
    setSubmitting(true);
    try {
      const { data: booking } = await api.post("/bookings", {
        bike_id: bike.id, plan, start_date: startDate, quantity: Math.max(1, Number(quantity)),
      });
      const { data: session } = await api.post("/checkout/session", {
        booking_id: booking.id, origin_url: window.location.origin,
      });
      window.location.href = session.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start booking.");
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
      <button onClick={() => navigate("/bikes")} className="flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-primary mb-6" data-testid="back-btn">
        <ChevronLeft className="w-4 h-4" /> Back to fleet
      </button>

      <div className="grid lg:grid-cols-5 gap-10">
        {/* Left: imagery + info */}
        <div className="lg:col-span-3">
          <div className="rounded-2xl overflow-hidden border border-border bg-secondary">
            <img src={bike.image} alt={bike.name} className="w-full h-[420px] object-cover" />
          </div>
          <div className="mt-8">
            <span className="label-caps text-primary capitalize">{bike.type}</span>
            <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">{bike.name}</h1>
            <p className="mt-4 text-muted-foreground text-lg leading-relaxed">{bike.description}</p>

            <div className="mt-8 grid grid-cols-3 gap-4">
              {[
                { icon: Zap, label: "Engine", value: bike.engine },
                { icon: Gauge, label: "Top speed", value: bike.top_speed },
                { icon: Fuel, label: "Range", value: bike.range_km },
              ].map((s) => (
                <div key={s.label} className="p-4 border border-border rounded-xl">
                  <s.icon className="w-5 h-5 text-primary" />
                  <p className="label-caps mt-2">{s.label}</p>
                  <p className="font-bold mt-0.5">{s.value || "—"}</p>
                </div>
              ))}
            </div>

            <div className="mt-8">
              <h3 className="font-bold text-lg mb-3">What's included</h3>
              <div className="grid sm:grid-cols-2 gap-2">
                {bike.features?.map((f) => (
                  <div key={f} className="flex items-center gap-2 text-muted-foreground">
                    <Check className="w-4 h-4 text-primary" /> {f}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right: booking widget */}
        <div className="lg:col-span-2">
          <div className="lg:sticky lg:top-24 border border-border rounded-2xl p-6 bg-white shadow-sm" data-testid="booking-widget">
            <p className="label-caps">Booking</p>
            <div className="mt-1 flex items-baseline gap-1">
              <span className="text-4xl font-black font-display text-primary">£{rate}</span>
              <span className="text-muted-foreground font-medium">/ {unit}</span>
            </div>

            <p className="label-caps mt-6 mb-2">Choose a plan</p>
            <div className="grid grid-cols-2 gap-2">
              {PLANS.map((p) => (
                <button
                  key={p.key}
                  data-testid={`plan-${p.key}`}
                  onClick={() => setPlan(p.key)}
                  className={`py-2.5 rounded-lg text-sm font-bold border transition-all ${
                    plan === p.key ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/50"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            <p className="label-caps mt-6 mb-2">Start date</p>
            <Input type="date" value={startDate} min={new Date().toISOString().split("T")[0]} onChange={(e) => setStartDate(e.target.value)} data-testid="start-date-input" className="bg-secondary/50" />

            <p className="label-caps mt-4 mb-2">Number of {unit}s</p>
            <Input type="number" min={1} value={quantity} onChange={(e) => setQuantity(e.target.value)} data-testid="quantity-input" className="bg-secondary/50" />

            <div className="mt-6 pt-6 border-t border-border space-y-2 text-sm">
              <div className="flex justify-between text-muted-foreground"><span>£{rate} × {Math.max(1, quantity)} {unit}{quantity > 1 ? "s" : ""}</span><span>£{total}</span></div>
              <div className="flex justify-between font-black text-lg pt-2"><span>Total</span><span data-testid="booking-total">£{total}</span></div>
            </div>

            <div className="mt-5 rounded-lg border border-border bg-secondary/40 p-3" data-testid="eligibility-box">
              <p className="label-caps mb-2">Before you hire</p>
              <ul className="space-y-1.5 text-xs text-muted-foreground">
                <li className="flex gap-2"><Check className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" /> Valid CBT certificate required</li>
                <li className="flex gap-2"><Check className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" /> Provisional licence accepted</li>
                <li className="flex gap-2"><Check className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" /> Minimum age 18 · licence checks apply</li>
                <li className="flex gap-2"><Check className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" /> Your own insurance required (not included)</li>
              </ul>
              <label className="mt-3 flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  data-testid="eligibility-checkbox"
                  className="mt-0.5 h-4 w-4 accent-[#2563EB] cursor-pointer"
                />
                <span className="text-xs font-semibold text-foreground">I have a valid CBT, I'm 18+, and I'll arrange my own insurance.</span>
              </label>
            </div>

            <Button
              onClick={handleBook}
              disabled={submitting || !bike.available || !agreed}
              data-testid="book-now-btn"
              className="w-full mt-5 h-12 bg-primary hover:bg-[#1D4ED8] text-white font-bold text-base"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : bookButtonLabel}
            </Button>
            <p className="text-xs text-muted-foreground text-center mt-3">Secure payment via Stripe · Cancel anytime</p>
            <div className="mt-4 flex items-start gap-2 rounded-lg bg-primary/10 p-3" data-testid="widget-promo">
              <Tag className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              <p className="text-xs font-semibold text-primary">Hire 4 weeks in a row & get 20% off your 5th week.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
